import { useRoles, useUserRoles } from '@/domains/access-control';
import { Badge } from '@/shared/ui/badge';
import type { RoleSummary } from '@skewb-works/contracts';
import { Lock } from 'lucide-react';
import { useMemo } from 'react';

interface UserRoleBadgesProps {
  userId: number;
}

/**
 * Renders the access-control roles a user currently holds as badges. Shows a
 * hyphen-style empty state when none are assigned, and a loading affordance
 * while the assignments resolve.
 */
export function UserRoleBadges({ userId }: UserRoleBadgesProps) {
  const { data: rolesData } = useRoles();
  const { data: userRolesData, isLoading: userRolesLoading } = useUserRoles(userId);

  const assignedRoles = useMemo<RoleSummary[]>(() => {
    const roles: RoleSummary[] = rolesData?.data ?? [];
    const assignedIds = new Set<number>(userRolesData?.roleIds ?? []);
    return roles.filter((role) => assignedIds.has(role.id));
  }, [rolesData, userRolesData]);

  if (userRolesLoading) {
    return <span className="text-sm text-muted-foreground">Loading…</span>;
  }

  if (assignedRoles.length === 0) {
    return <span className="text-sm text-muted-foreground">No roles assigned</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {assignedRoles.map((role) => (
        <Badge key={role.id} variant="secondary" className="gap-1">
          {role.isSystem && <Lock className="w-3 h-3" />}
          {role.name}
        </Badge>
      ))}
    </div>
  );
}
